import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Skeleton, Stack, Typography } from "@mui/material";
import MUIDataTable, { MUIDataTableOptions } from "mui-datatables";
import { ThemeProvider } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import AttToken from "../../helpers/attToken";
import {
  RequesterTicketStatus,
  RequesterTicketsFilters,
  TicketsV3,
} from "../../service/Tickets";
import { getMuiTheme, tableOptions } from "../TableHelpers/options";
import { StyledDiv } from "../TableHelpers/TableStyle";
import RequesterFilters from "./RequesterFilters";
import ModalTicket from "./Modals/ModalTicket";
import { trackEventMatomo } from "../../helpers/matomo";
import { getTicketStatusPresentation } from "../../helpers/ticketStatusPresentation";

const ticketsService = new TicketsV3();

const normalizeTicketId = (ticketId: string) => ticketId.split("_-")[0];

const RequesterTableContainer = styled(StyledDiv)`
  width: 100%;

  .MuiTablePagination-selectRoot,
  [class*="MUIDataTablePagination-selectRoot"] {
    width: auto !important;
    min-width: auto !important;
    flex: 0 0 auto;
  }
`;

export default function Solicitante() {
  const { t } = useTranslation("translation");
  const [currentPage, setCurrentPage] = useState(0);
  const [pageCursors, setPageCursors] = useState<Record<number, string | null>>({ 0: null });
  const [limit, setLimit] = useState(10);
  const [filters, setFilters] = useState<RequesterTicketsFilters | undefined>();
  const [filtersRequestVersion, setFiltersRequestVersion] = useState(0);
  const [openModal, setOpenModal] = useState(false);
  const [selectedTicketId, setSelectedTicketId] = useState<string | null>(null);
  const currentCursor = pageCursors[currentPage] ?? null;

  const { data, isLoading, isFetching, isError, refetch } = useQuery({
    queryKey: ["GET_CHAMADOS_SOLICITANTE", limit, currentCursor, filters, filtersRequestVersion],
    queryFn: async () => {
      const token = await AttToken();
      if (!token) throw new Error("Token não encontrado");
      const response = await ticketsService.getByRequester(token, token.username, currentCursor, limit, filters);
      if (!response?.success) throw new Error("Erro ao buscar chamados");
      return response.data;
    },
    retry: 0,
    refetchOnWindowFocus: false,
    refetchOnMount: 'always',
  });

  const statusTag = (status: RequesterTicketStatus | string) => {
    const presentation = getTicketStatusPresentation(status);

    if (!presentation) {
      return <span className="tag is-info">{status || "-"}</span>;
    }

    return <span className={presentation.tagClassName}>{t(presentation.labelKey)}</span>;
  };

  const applyFilters = (nextFilters: RequesterTicketsFilters | undefined) => {
    setCurrentPage(0);
    setPageCursors({ 0: null });
    setFilters(nextFilters);
    setFiltersRequestVersion(previous => previous + 1);
  };

  const columns = [
    {
      name: "id",
      label: "ID",
      options: { customBodyRender: (value: string) => normalizeTicketId(value) },
    },
    {
      name: "inicio",
      label: t("notifications.ticket.createdAt"),
      options: { customBodyRender: (value: number) => value ? new Date(value).toLocaleString() : "" },
    },
    { name: "nome", label: t("notifications.ticket.tickets") },
    {
      name: "responsavelNome",
      label: t("notifications.ticket.responsable"),
      options: { customBodyRender: (value: string) => value || "" },
    },
    {
      name: "dataPrevisao",
      label: t("notifications.ticket.limitDate"),
      options: { customBodyRender: (value: number) => value ? new Date(value).toLocaleString() : "" },
    },
    {
      name: "status",
      label: t("status"),
      options: { customBodyRender: (value: RequesterTicketStatus) => statusTag(value) },
    },
  ];

  const translatedTextLabels: MUIDataTableOptions = {
    textLabels: {
      body: { noMatch: t("notifications.requester.noTickets"), toolTip: t("table.toolTip"), columnHeaderTooltip: column => `${t("table.columnHeaderTooltip")} ${column.label}` },
      pagination: { next: t("table.next"), previous: t("table.previous"), rowsPerPage: t("table.rowsPerPage"), displayRows: t("table.displayRows") },
      toolbar: { search: t("table.search"), filterTable: t("table.filterTable") },
      filter: { title: t("table.title"), reset: t("table.reset") },
      viewColumns: { title: t("table.viewColumnsTitle") },
    },
    onChangePage: (page: number) => {
      if (page < currentPage) {
        setCurrentPage(page);
        return;
      }

      const nextCursor = data?.lastIndex;
      if (page > currentPage && nextCursor) {
        setPageCursors(previous => ({ ...previous, [page]: nextCursor }));
        setCurrentPage(page);
      }
    },
    onChangeRowsPerPage: (numberOfRows: number) => {
      setCurrentPage(0);
      setPageCursors({ 0: null });
      setLimit(numberOfRows);
    },
    serverSide: true,
    rowsPerPageOptions: [10, 15, 50, 100],
    page: currentPage,
    rowsPerPage: limit,
    count: data?.totalItens ?? 0,
    search: false,
    sort: false,
    onRowClick: (rowData: string[]) => {
      trackEventMatomo("chamados do solicitante", "click", "row", "abre chamado");
      setSelectedTicketId(rowData[0]);
      setOpenModal(true);
    },
  };

  return (
    <Stack direction="column" justifyContent="center" alignItems="center" spacing={2} width="100%" sx={{ paddingBottom: 2 }}>
      {openModal && <ModalTicket openModal={openModal} setOpenModal={setOpenModal} id={selectedTicketId} mutate={refetch} requesterMode />}
      <RequesterFilters disabled={isLoading || isFetching} onApply={applyFilters} />
      {isLoading || isFetching ? <Skeleton variant="rounded" height={400} width="100%" /> : (
        <Stack direction="column" justifyContent="flex-start" spacing={1} alignItems="flex-end" width="100%">
          {isError ? (
            <Typography color="textDisabled" variant="h5">{t("notifications.requester.errorFetchingTickets")}</Typography>
          ) : !data?.chamados.length ? (
            <Typography color="textDisabled" variant="h5">{t("notifications.requester.noTickets")}</Typography>
          ) : (
            <RequesterTableContainer>
              <ThemeProvider theme={getMuiTheme()}>
                <MUIDataTable
                  title={t("notifications.requester.tableTitle")}
                  data={data.chamados}
                  columns={columns}
                  options={{ ...tableOptions, ...translatedTextLabels }}
                />
              </ThemeProvider>
            </RequesterTableContainer>
          )}
        </Stack>
      )}
    </Stack>
  );
}